import React from 'react'
import { Steps } from 'antd';
import Avatar from 'components/Avatar'
import CustomTag from 'components/CustomTag'
import Flex from 'components/Flex'
import AwardsIconWork from 'components/icons/AwardsIconWork'
import ApartmentIcon from 'components/icons/ApartmentIcon'
import FileReaderVisibleOnly from 'components/Uploader/FileReaderVisibleOnly'
import {
  BoldTextDate,
  EditorContent,
  GreyText,
  NameAdd1,
  RewardTiteNew,
  SecondaryHeading,
  SecondaryHeadingNew,
  StyledP,
  SubFLex,
  TextLight1,
  VerticalDivider,
} from 'modules/Resume/Style/style'
import moment from 'moment'
import { COLORS } from 'theme'
import { ExclamationCircleFilled } from '@ant-design/icons'
import styled from 'styled-components'

const PendingIcon = styled(ExclamationCircleFilled)`
  color: #eb5d60;
  font-size: 16px;
  margin-left: 8px;
`

// Same employer = same corporate record, or failing that the same company name.
// Entries picked from the "Others" free-text option all share the corporateId
// "OTHERS", so that id alone cannot identify an employer.
const employerKey = item => {
  const corporateId = item?.corporateId
  if (corporateId && corporateId !== 'OTHERS') return `id:${corporateId}`
  const organization = item?.organization?.trim()?.toLowerCase()
  return organization ? `name:${organization}` : null
}

// Groups positions by employer, keeping the resume's own order. An entry with
// no company name identifies no employer, so it stands on its own rather than
// being merged with every other nameless entry.
const groupByEmployer = (workExp = []) => {
  const groups = []
  const groupIndexByKey = new Map()

  workExp.filter(Boolean).forEach(item => {
    const key = employerKey(item)
    const existingIndex = key ? groupIndexByKey.get(key) : undefined

    if (existingIndex !== undefined) {
      groups[existingIndex].push(item)
      return
    }
    if (key) groupIndexByKey.set(key, groups.length)
    groups.push([item])
  })

  return groups
}

const WorkExpSection = ({ promotionData, pendingData, studentDetails }) => {
  console.log("pendingData", pendingData);
  const hasFieldPending = (item, fieldName) => {
    return item?._pendingFields && item._pendingFields[fieldName] !== undefined
  }

  const approvedWorkExp = studentDetails?.resume?.workExperience || []
  const pendingWorkExp = pendingData?.resume?.workExperience || []

  // Create a map of pending work experience by ID
  const pendingMap = new Map()
  pendingWorkExp.forEach(pendingItem => {
    if (pendingItem.id && !pendingItem.id.startsWith('temp-')) {
      pendingMap.set(pendingItem.id, pendingItem)
    }
  })

  // Check if data is already grouped or needs grouping
  const isGrouped = approvedWorkExp.length > 0 && Array.isArray(approvedWorkExp[0])

  // Convert to groups if not already grouped. A group is one *employer* — the
  // card shows the group's company once and lists its positions as a promotion
  // timeline — so a flat list has to be grouped by company. Wrapping the whole
  // list in a single group (what this used to do) printed only the first
  // company's name and hung every other company's role under it.
  let workExpGroups = isGrouped ? approvedWorkExp : groupByEmployer(approvedWorkExp)

  // Merge pending changes into work experience
  const mergedWorkExpGroups = workExpGroups.map(group => {
    // Ensure group is an array
    const groupArray = Array.isArray(group) ? group : [group]

    return groupArray.map(item => {
      if (!item) return item
      const pendingItem = pendingMap.get(item?.id)
      if (pendingItem && pendingItem._pendingFields) {
        // Merge pending fields into the item
        const merged = { ...item }

        let requestId = null
        const firstFieldKey = Object.keys(pendingItem._pendingFields)[0]
        if (firstFieldKey && pendingItem._pendingFields[firstFieldKey]._requestId) {
          requestId = pendingItem._pendingFields[firstFieldKey]._requestId
        }

        Object.keys(pendingItem._pendingFields).forEach(fieldName => {
          const value = pendingItem._pendingFields[fieldName].value

          if (fieldName === 'companyName') {
            merged.organization = value
            merged.companyName = value
          } else {
            merged[fieldName] = value
          }
        })
        merged._pendingFields = pendingItem._pendingFields
        merged._requestId = requestId
        return merged
      }
      return item
    })
  })

  const newPendingWorkExp = pendingWorkExp.filter(item =>
    !item.id || item.id.startsWith('temp-') || item._isNew
  )

  const allWorkExpGroups = [...mergedWorkExpGroups]
  newPendingWorkExp.forEach(item => {
    allWorkExpGroups.push([{ ...item, _requestId: true, _isNew: true }])
  })

  const countRichTextCharacters = richText => {
    // Remove HTML tags using a regular expression
    const plainText = richText?.replace(/<[^>]*>/g, '')?.replace(/&nbsp;/g, ' ');

    // Count the characters in the plain text
    const characterCount = plainText?.length || 0; // Set to 0 if undefined

    return characterCount
  };

  // Helper to check if dates should be displayed
  const shouldShowDates = (item) => {
    return item?.started_in || item?.ended_in
  }

  // Helper to render dates safely
  const renderDateRange = (item, isCurrentlyWorking) => {
    if (!shouldShowDates(item)) return null

    const start = item?.started_in ? moment(item?.started_in, 'MM/YYYY')?.format('MMM YYYY') : ''
    const end = isCurrentlyWorking ? 'Present' : (item?.ended_in ? moment(item?.ended_in, 'MM/YYYY')?.format('MMM YYYY') : '')

    let dateStr = ''
    if (start && end) {
      dateStr = `${start} - ${end}`
    } else if (start) {
      dateStr = start
    } else if (end) {
      dateStr = end
    }

    const duration = isCurrentlyWorking
      ? DateDiff(item?.started_in, undefined)
      : DateDiff(item?.started_in, item?.ended_in)

    return `${dateStr} ${duration ? `(${duration})` : ''}`
  }


  const DateDiff = (start, end) => {
    if (!start) return '';

    const StartSplit = start?.split('/')
    if (!StartSplit || StartSplit.length < 2) return '';

    const EndSplit = end ? end?.split('/') : null
    if (end && (!EndSplit || EndSplit.length < 2)) return '';

    const startDate = new Date(StartSplit[1], StartSplit[0], 1)
    const endDate = end && EndSplit ? new Date(EndSplit[1], EndSplit[0], 1) : new Date()
    const diffInMonths =
      (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth()) + 1

    if (diffInMonths >= 12) {
      const years = Math.floor(diffInMonths / 12)
      const months = diffInMonths % 12
      return `${years === 1 ? years + ' year' : years > 1 ? years + ' years' : ''} ${months === 1 ? months + ' month' : months > 1 ? months + ' months' : ''}`;
    } else {
      return `${diffInMonths === 1 ? diffInMonths + ' month' : diffInMonths > 1 ? diffInMonths + ' months' : ''}`;
    }
  }

  const DateDiffArray = (positions) => {
    const calculateDurationInMonths = (start, end) => {
      if (!start) return 0;

      const StartSplit = start?.split('/');
      if (!StartSplit || StartSplit.length < 2) return 0;

      const EndSplit = end ? end?.split('/') : null;
      if (end && (!EndSplit || EndSplit.length < 2)) return 0;

      const startDate = new Date(StartSplit[1], StartSplit[0], 1);
      const endDate = end && EndSplit ? new Date(EndSplit[1], EndSplit[0], 1) : new Date();
      return (
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth()) + 1
      );
    };

    const totalDurationInMonths = positions.reduce((total, position) => {
      const end = position.isCurrentlyWorking ? undefined : position.ended_in;
      return total + calculateDurationInMonths(position.started_in, end);
    }, 0);

    if (totalDurationInMonths >= 12) {
      const years = Math.floor(totalDurationInMonths / 12);
      const months = totalDurationInMonths % 12;
      return `${years === 1 ? years + ' year' : years > 1 ? years + ' years' : ''} ${months === 1 ? months + ' month' : months > 1 ? months + ' months' : ''}`;
    } else {
      return `${totalDurationInMonths === 1 ? totalDurationInMonths + ' month' : totalDurationInMonths > 1 ? totalDurationInMonths + ' months' : ''}`;
    }
  };



  return (
    <>
      <style>
        {`
        .ant-steps-vertical > .ant-steps-item > .ant-steps-item-container > .ant-steps-item-tail::after {
          width: 1.34px;  // Set the width of the progress line
          height: 100%;
          background-color: #D9D9D9;  // Set the color of the progress line
        }
      `}
      </style>

      <SubFLex column gap="25px">
        {allWorkExpGroups?.map((experienceGroup, groupIndex) => (
          <>
            {experienceGroup?.length > 1 ? (

              <SubFLex gap="12px">
                <div>
                  <Avatar
                    background="none"
                    src={''}
                    size={48}
                    icon={<ApartmentIcon />}
                    boxShadow={true}
                    padding="5px"
                    shape="square"
                  />
                </div>
                <SubFLex column style={{ width: '90%' }}>
                  <Flex alignCenter spaceBetween style={{ width: '100%' }}>
                    <SecondaryHeadingNew fontWeight="600" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center' }}>
                      <span>{experienceGroup?.[0]?.organization}{experienceGroup?.[0]?.industry ? ` - ${experienceGroup?.[0]?.industry}` : ''}</span>
                      {/* {(hasFieldPending(experienceGroup?.[0], 'organization') || hasFieldPending(experienceGroup?.[0], 'industry')) && <PendingIcon />} */}
                    </SecondaryHeadingNew>
                    <div style={{ marginTop: '-1px' }}>
                      {experienceGroup?.some(item => item?._requestId) && (
                        <CustomTag
                          message="Verification Pending"
                          bordered={true}
                          background={'#eb5d60'}
                          border={'#eb5d60'}
                          color={'#fff'}
                        />
                      )}
                    </div>
                  </Flex>
                  <GreyText color={COLORS.GREY_T_60}>
                    {DateDiffArray(experienceGroup)}
                  </GreyText>
                  <Steps
                    key={groupIndex + "experience"}
                    current={-1}  // Set current to -1 to hide the active step
                    direction="vertical"
                    progressDot={(dot, { index, status }) => {
                      const dotStyle = {
                        width: '9px',
                        height: '9px',
                        borderRadius: '50%',  // Make it a circle
                        background: '#D9D9D9',
                      };

                      return (
                        <div style={dotStyle}>
                          {dot}
                        </div>
                      );
                    }}
                    items={experienceGroup.map((expItem) => ({
                      title: (
                        <SubFLex gap="10px" flexStart key={expItem?.id} style={{ paddingTop: '7px' }}>
                          <SecondaryHeading fontWeight="600" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center' }}>
                            <span>{expItem?.title}</span>
                            {/* {hasFieldPending(expItem, 'title') && <PendingIcon />} */}
                          </SecondaryHeading>
                          {/* {expItem?.jobType == "FULL_TIME" ?
                    <CustomTag
                      message={"Full Time"}
                      border='#CCCFD6'
                      bordered={true}
                      background='#F2F3F5'
                      colorType="#808697"
                      />
                     :
                     <CustomTag
                      message={"Part Time"}
                      border='#CCCFD6'
                      bordered={true}
                      background='#F2F3F5'
                      colorType="#808697"
                      />
                    } */}
                        </SubFLex>
                      ),
                      description: (
                        <>
                          <Flex alignItems="flex-start" gap="10px">
                            {expItem?.isCurrentlyWorking ? (
                              <>
                                {shouldShowDates(expItem) && (
                                  <GreyText color={"#3249D7"}>
                                    {renderDateRange(expItem, true)}
                                  </GreyText>
                                )}
                              </>
                            ) : (
                              <>
                                {shouldShowDates(expItem) && (
                                  <GreyText color={COLORS.GREY_T_60}>
                                    {renderDateRange(expItem, false)}
                                  </GreyText>
                                )}
                              </>
                            )}

                            {expItem?.jobType && (
                              <CustomTag
                                message={expItem?.jobType === "FULL_TIME" ? "Full Time" : "Part Time"}
                                border="#CCCFD6"
                                bordered={true}
                                background="#F2F3F5"
                                colorType="#808697"
                                style={{
                                  marginLeft: '6px',
                                  transform: 'translateY(-3px)'   // 🔥 THIS is the real fix
                                }}
                              />
                            )}
                          </Flex>
                          {expItem?.skill_set?.length > 0 &&
                            <SecondaryHeading fontWeight="600">
                              Skills
                            </SecondaryHeading>}
                          {/* Additional details like skills, function, role, etc. can be added here */}
                          <SubFLex centerVertically gap="5px" marginBottom="15px" wrap>
                            {expItem?.skill_set?.map((value, key) => (
                              <CustomTag
                                message={value?.name}
                                bordered={true}
                                background={'#F2F3F5'}
                                border={'#F2F3F5'}
                                key={key + "skillSet"}
                              />
                            ))}
                          </SubFLex>
                          <Flex centerVertically>
                            {/* {expItem?.function &&
    <>
      <NameAdd1 column flexStart>
        <TextLight1 color={COLORS.GREY_T_20}>Function</TextLight1>
        <BoldTextDate color={COLORS.GREY_T_20} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span>{expItem?.function}</span>
          
        </BoldTextDate>
      </NameAdd1>
      {expItem?.role && <VerticalDivider type="vertical" />}
    </>
  } */}
                            {expItem?.role &&
                              <>
                                <NameAdd1 column flexStart>
                                  <TextLight1 color={COLORS.GREY_T_20}>Role</TextLight1>
                                  <BoldTextDate color={COLORS.GREY_T_20} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: '5px' }}  >
                                    <span>{expItem?.role}</span>
                                    {/* {hasFieldPending(expItem, 'role') && <PendingIcon />} */}
                                  </BoldTextDate>
                                </NameAdd1>
                                {expItem?.industry && <VerticalDivider type="vertical" />}
                              </>
                            }
                            {expItem?.industry &&
                              <NameAdd1 column flexStart>
                                <TextLight1 color={COLORS.GREY_T_20}>Industry</TextLight1>
                                <BoldTextDate color={COLORS.GREY_T_20} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <span>{expItem?.industry}</span>
                                  {/* {hasFieldPending(expItem, 'industry') && <PendingIcon />} */}
                                </BoldTextDate>
                              </NameAdd1>
                            }
                          </Flex>
                          {countRichTextCharacters(expItem?.description) > 0 ?
                            <Flex gap="5px" alignItems="flex-start">
                              <StyledP>
                                <EditorContent
                                  dangerouslySetInnerHTML={{
                                    __html: expItem?.description || '',
                                  }}
                                />
                              </StyledP>
                              {/* {hasFieldPending(expItem, 'description') && <PendingIcon style={{ marginTop: '5px' }} />} */}
                            </Flex>
                            : null}
                          {expItem?.certificate && Object.keys(expItem?.certificate).length > 0 && (
                            <FileReaderVisibleOnly file={expItem?.certificate} />
                          )}
                          {expItem?.awards?.length > 0 && (
                            <>
                              <br></br>
                              <RewardTiteNew>Rewards and Recognition </RewardTiteNew>
                            </>
                          )}
                          {expItem?.awards?.map((value, key) => (
                            <SubFLex column gap="25px" marginBottom="10px" key={key + "award"}>
                              <SubFLex gap="12px">
                                <div>
                                  <Avatar
                                    background="none"
                                    src={''}
                                    size={48}
                                    icon={<AwardsIconWork />}
                                    boxShadow={true}
                                    padding="5px"
                                  />
                                </div>
                                <SubFLex column style={{ width: '90%' }}>
                                  <SecondaryHeading>{value?.title}</SecondaryHeading>
                                  <GreyText color={COLORS.GREY_T_60}>
                                    {moment(value?.awarded_on).format('DD MMM YYYY')}
                                  </GreyText>
                                  {countRichTextCharacters(value?.description) > 0 ?
                                    <StyledP>
                                      <EditorContent
                                        dangerouslySetInnerHTML={{
                                          __html: value?.description || '',
                                        }}
                                      />
                                    </StyledP>
                                    : null}
                                  {value?.certificate && (
                                    <FileReaderVisibleOnly file={value?.certificate} />
                                  )}
                                </SubFLex>
                              </SubFLex>
                            </SubFLex>
                          ))}
                        </>
                      ),
                    }))}
                  />
                </SubFLex>
              </SubFLex>

            ) :
              <>
                {experienceGroup?.map((expItem, index) => (

                  <SubFLex gap="12px" key={index + "experience"}>
                    <div>
                      <Avatar
                        background="none"
                        src={''}
                        size={48}
                        icon={<ApartmentIcon />}
                        boxShadow={true}
                        padding="5px"
                        shape="square"
                      />
                    </div>
                    <SubFLex column style={{ width: '90%' }}>
                      <Flex alignCenter spaceBetween style={{ width: '100%' }}>
                        <SecondaryHeadingNew fontWeight="600" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center' }}>
                          {/* the entry being rendered, not the group's first — they
                              are the same here, but reading item[0] is what hid the
                              other companies in the first place */}
                          <span>{expItem?.organization}{expItem?.industry ? ` - ${expItem?.industry}` : ''}</span>
                          {/* {(hasFieldPending(expItem, 'organization') || hasFieldPending(expItem, 'industry')) && <PendingIcon />} */}
                        </SecondaryHeadingNew>
                        <div style={{ marginTop: '-1px' }}>
                          {experienceGroup?.some(item => item?._requestId) && (
                            <CustomTag
                              message="Verification Pending"
                              bordered={true}
                              background={'#eb5d60'}
                              border={'#eb5d60'}
                              color={'#fff'}
                            />
                          )}
                        </div>
                      </Flex>
                      <GreyText color={COLORS.GREY_T_60}>
                        {shouldShowDates(expItem) && (expItem?.isCurrentlyWorking
                          ? DateDiff(expItem?.started_in, undefined)
                          : DateDiff(expItem?.started_in, expItem?.ended_in))}
                      </GreyText>
                      <SubFLex gap="10px" flexStart key={expItem?.id} style={{ paddingTop: '7px' }}>
                        <SecondaryHeading fontWeight="600" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center' }}>
                          <span>{expItem?.title}</span>
                          {/* {hasFieldPending(expItem, 'title') && <PendingIcon />} */}
                        </SecondaryHeading>
                        {/* {expItem?.jobType == "FULL_TIME" ?
              <CustomTag
                message={"Full Time"}
                border='#CCCFD6'
                bordered={true}
                background='#F2F3F5'
                colorType="#808697"
                />
               :
               <CustomTag
                message={"Part Time"}
                border='#CCCFD6'
                bordered={true}
                background='#F2F3F5'
                colorType="#808697"
                />
              } */}
                      </SubFLex>

                      <Flex alignItems="flex-start" gap="10px">
                        {shouldShowDates(expItem) && expItem?.isCurrentlyWorking ? (
                          <GreyText color={"#3249D7"}>
                            {renderDateRange(expItem, true)}
                          </GreyText>
                        ) : shouldShowDates(expItem) ? (
                          <GreyText color={COLORS.GREY_T_60}>
                            {renderDateRange(expItem, false)}
                          </GreyText>
                        ) : null}

                        {expItem?.jobType && (
                          <CustomTag
                            message={expItem?.jobType === "FULL_TIME" ? "Full Time" : "Part Time"}
                            border="#CCCFD6"
                            bordered={true}
                            background="#F2F3F5"
                            colorType="#808697"
                            style={{
                              marginLeft: '6px',
                              transform: 'translateY(-3px)'   // 🔥 THIS is the real fix
                            }}
                          />
                        )}
                      </Flex>
                      {expItem?.skill_set?.length > 0 &&
                        <SecondaryHeading fontWeight="600">
                          Skills
                        </SecondaryHeading>}
                      <SubFLex centerVertically gap="5px" marginBottom="15px" wrap>
                        {expItem?.skill_set?.map((value, key) => (
                          <CustomTag
                            message={value?.name}
                            bordered={true}
                            background={'#F2F3F5'}
                            border={'#F2F3F5'}
                            key={key + "skillSet"}
                          />
                        ))}
                      </SubFLex>
                      <Flex centerVertically>
                        {/* {expItem?.function &&
        <>
        <NameAdd1 column flexStart>
          <TextLight1 color={COLORS.GREY_T_20}>Function</TextLight1>
          <BoldTextDate color={COLORS.GREY_T_20} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span>{expItem?.function}</span>
            
          </BoldTextDate>
        </NameAdd1>
        {expItem?.role && <VerticalDivider type="vertical" />}
        </>
        } */}
                        {expItem?.role &&
                          <>
                            <NameAdd1 column flexStart>
                              <TextLight1 color={COLORS.GREY_T_20}>Role</TextLight1>
                              <BoldTextDate color={COLORS.GREY_T_20} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span>{expItem?.role}</span>
                                {/* {hasFieldPending(expItem, 'role') && <PendingIcon />} */}
                              </BoldTextDate>
                            </NameAdd1>
                            {expItem?.industry && <VerticalDivider type="vertical" />}
                          </>
                        }
                        {expItem?.industry &&
                          <NameAdd1 column flexStart>
                            <TextLight1 color={COLORS.GREY_T_20}>Industry</TextLight1>
                            <BoldTextDate color={COLORS.GREY_T_20} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span>{expItem?.industry}</span>
                              {/* {hasFieldPending(expItem, 'industry') && <PendingIcon />} */}
                            </BoldTextDate>
                          </NameAdd1>
                        }
                      </Flex>
                      {countRichTextCharacters(expItem?.description) > 0 ?
                        <Flex gap="5px" alignItems="flex-start">
                          <StyledP>
                            <EditorContent
                              dangerouslySetInnerHTML={{
                                __html: expItem?.description || '',
                              }}
                            />
                          </StyledP>
                          {/* {hasFieldPending(expItem, 'description') && <PendingIcon style={{ marginTop: '5px' }} />} */}
                        </Flex>
                        : null}
                      {/* {expItem?.certificate && Object.keys(expItem?.certificate).length > 0 && (
                <FileReaderVisibleOnly file={expItem?.certificate} />
              )} */}

                      {expItem?.certificate && Object.keys(expItem?.certificate).length > 0 && (
                        <>
                          {hasFieldPending(expItem, 'certificate') ? (
                            <div
                              style={{
                                background: '#FFE5E6',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: '8px',
                                marginBottom: '8px'
                              }}
                            >
                              <span style={{ fontSize: '14px', color: '#1F2937' }}>
                                Marksheet Submitted - <span style={{ color: '#3249D7', fontWeight: 500 }}>{expItem?.certificate?.name || 'Document.jpg'}</span>
                              </span>
                              <button
                                onClick={() => {
                                  const newWindow = window.open(expItem?.certificate?.url, '_blank', 'noopener,noreferrer')
                                  if (newWindow) newWindow.opener = null
                                }}
                                style={{
                                  background: '#3249D7',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '6px 20px',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  cursor: 'pointer'
                                }}
                              >
                                View
                              </button>
                            </div>
                          ) : (
                            <FileReaderVisibleOnly file={expItem?.certificate} />
                          )}
                        </>
                      )}

                      {expItem?.awards?.length > 0 && (
                        <>
                          <br></br>
                          <RewardTiteNew>Rewards and Recognition </RewardTiteNew>
                        </>
                      )}

                      {expItem?.awards?.map((value, key) => (
                        <SubFLex column gap="25px" marginBottom="10px" key={key + "award"}>
                          <SubFLex gap="12px">
                            <div>
                              <Avatar
                                background="none"
                                src={''}
                                size={48}
                                icon={<AwardsIconWork />}
                                boxShadow={true}
                                padding="5px"
                              />
                            </div>
                            <SubFLex column style={{ width: '90%' }}>
                              <SecondaryHeading>{value?.title}</SecondaryHeading>
                              <GreyText color={COLORS.GREY_T_60}>
                                {moment(value?.awarded_on).format('DD MMM YYYY')}
                              </GreyText>
                              {countRichTextCharacters(value?.description) > 0 ?
                                <StyledP>
                                  <EditorContent
                                    dangerouslySetInnerHTML={{
                                      __html: value?.description || '',
                                    }}
                                  />
                                </StyledP>
                                : null}
                              {value?.certificate && (
                                <FileReaderVisibleOnly file={value?.certificate} />
                              )}
                            </SubFLex>
                          </SubFLex>
                        </SubFLex>
                      ))}
                    </SubFLex>
                  </SubFLex>

                ))}
              </>
            }
          </>
        ))}
      </SubFLex>
    </>
  )

}

export default WorkExpSection